import Vue from "vue";
import VueI18n, { LocaleMessages } from "vue-i18n";
import { parseLocaleCode } from "@/models/Locale";

Vue.use(VueI18n);

function loadLocaleMessages(): LocaleMessages {
    const locales = require.context(
        "./locales",
        true,
        /[A-Za-z0-9-_,\s]+\.json$/i
    );
    const messages: LocaleMessages = {};
    locales.keys().forEach((key) => {
        const matched = key.match(/([A-Za-z0-9-_]+)\./i);
        if (matched && matched.length > 1) {
            const locale = matched[1];
            messages[locale] = locales(key);
        }
    });
    return messages;
}

const dateTimeFormats: VueI18n.DateTimeFormats = {
    en: {
        short: {
            year: "numeric",
            month: "short",
            day: "numeric",
        },
        dateTime: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
        },
        long: {
            year: "numeric",
            month: "long",
            day: "numeric",
            weekday: "long",
            hour: "numeric",
            minute: "numeric",
        },
    },
    de: {
        short: {
            year: "numeric",
            month: "short",
            day: "numeric",
        },
        dateTime: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
        },
        long: {
            year: "numeric",
            month: "long",
            day: "numeric",
            weekday: "long",
            hour: "numeric",
            minute: "numeric",
            hour12: false,
        },
    },
};

const numberFormats: VueI18n.NumberFormats = {
    "en-US": {
        currency: {
            style: "currency",
            currency: "USD",
        },
        decimal: {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
        percent: {
            style: "percent",
            useGrouping: false,
        },
    },
    "en-GB": {
        currency: {
            style: "currency",
            currency: "GBP",
        },
        decimal: {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
        percent: {
            style: "percent",
            useGrouping: false,
        },
    },
    en: {
        currency: {
            style: "currency",
            currency: "USD",
        },
        decimal: {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
        percent: {
            style: "percent",
            useGrouping: false,
        },
    },
    de: {
        currency: {
            style: "currency",
            currency: "EUR",
            currencyDisplay: "symbol",
        },
        decimal: {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
        percent: {
            style: "percent",
            useGrouping: false,
        },
    },
    fr: {
        currency: {
            style: "currency",
            currency: "EUR",
            currencyDisplay: "symbol",
        },
        decimal: {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
        percent: {
            style: "percent",
            useGrouping: false,
        },
    },
    pt: {
        currency: {
            style: "currency",
            currency: "EUR",
            currencyDisplay: "symbol",
        },
        decimal: {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
        percent: {
            style: "percent",
            useGrouping: false,
        },
    },
    es: {
        currency: {
            style: "currency",
            currency: "EUR",
            currencyDisplay: "symbol",
        },
        decimal: {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
        percent: {
            style: "percent",
            useGrouping: false,
        },
    },
};

// Create VueI18n instance with options
const i18n = new VueI18n({
    fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || "en",
    messages: loadLocaleMessages(),
    dateTimeFormats: dateTimeFormats,
    numberFormats: numberFormats,
    silentFallbackWarn: true,
});

// Set initial locale based on browser settings or default
const navigatorLanguage = navigator.language;
const navigatorLocale = parseLocaleCode(navigatorLanguage);

if(navigatorLocale && i18n.availableLocales.includes(navigatorLocale.baseName)) {
    i18n.locale = navigatorLocale.baseName;
}
else if(navigatorLocale && i18n.availableLocales.includes(navigatorLocale.language)) {
    i18n.locale = navigatorLocale.language;
}
else {
    i18n.locale = i18n.fallbackLocale as string;
}

export default i18n;
